/**
 * XPBarChart — CSS-only 7-bar weekly XP chart. No chart libraries.
 * data: array of 7 raw XP values (Mon–Sun). Heights are calculated relative
 * to the week's maximum daily XP, so the tallest bar always fills the chart.
 */

import React from 'react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface XPBarChartProps {
  /** Raw XP earned per day, Mon=index 0 through Sun=index 6. */
  data: number[];
}

export const XPBarChart: React.FC<XPBarChartProps> = ({ data }) => {
  const jsDay = new Date().getDay(); // 0=Sun,1=Mon…6=Sat
  const todayIdx = (jsDay + 6) % 7; // convert to Mon=0 index

  // Normalise to exactly 7 values; avoid divide-by-zero
  const xpThisWeek = [...data.slice(0, 7), 0, 0, 0, 0, 0, 0, 0].slice(0, 7) as number[];
  const maxXP = Math.max(...xpThisWeek, 1);

  return (
    <div>
      <p className="font-montserrat font-bold text-sm text-neutral-800 mb-3">XP This Week</p>

      <div className="flex items-end gap-1.5" style={{ height: '64px' }}>
        {xpThisWeek.map((xp, i) => {
          const isToday = i === todayIdx;
          const isFuture = i > todayIdx;
          const hasXP = xp > 0;

          // Height as percentage of the 48px bar area
          const heightPct = (xp / maxXP) * 100;
          const heightPx = hasXP ? Math.max(Math.round(heightPct * 0.48), 4) : 4;

          // Colour rules (spec):
          // today + XP → teal
          // past + XP → primary blue gradient
          // zero or future → grey border (#e8e8ee)
          let background: string;
          if (!hasXP || isFuture) {
            background = '#e8e8ee';
          } else if (isToday) {
            background = 'linear-gradient(180deg, #00b89a 0%, #00c2a8 100%)';
          } else {
            background = 'linear-gradient(180deg, #5b6cff 0%, #7c8eff 100%)';
          }

          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end" style={{ height: '48px' }}>
                <div
                  className="w-full rounded-t-md"
                  style={{
                    height: `${heightPx}px`,
                    background,
                    transformOrigin: 'bottom',
                    transition: 'height 0.7s ease',
                  }}
                />
              </div>
              <span
                className="font-open-sans text-neutral-400 leading-none"
                style={{ fontSize: '10px' }}
              >
                {DAYS[i]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
